import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { USERNAME_STYLES, AVATAR_FRAMES, BANNER_BADGES, CARD_BACKGROUNDS, WEAPONS, PROFILE_THEMES, BACKGROUND_VIDEOS, PROFILE_BUNDLES } from "../constants/loadout";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import AnimatedBackground from "../components/AnimatedBackground";
import { 
  Crown, Lock, X, ChevronLeft, ChevronRight, Settings, Play, Music, 
  MessageSquare, Clock, Sparkles, Shield, Award, Users, CheckCircle2, 
  Zap, ArrowRight, Eye, ShoppingCart, Video, Package, Flame, Heart, Star, Download
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import "../components/VipLoadout.css";

const MINI_TABS = [
  { id: "username_style", label: "USERNAME STYLE" },
  { id: "avatar_frame", label: "AVATAR FRAMES" },
  { id: "banner_badge", label: "BANNER BADGE" },
  { id: "card_background", label: "CARD BACKGROUND" },
  { id: "favorite_weapon", label: "FAVORITE WEAPON" },
];

const FULL_TABS = [
  { id: "profile_bundles", label: "PACHETE DISCORD & VIP" },
  { id: "background_video", label: "VIDEO FUNDAL PROFIL" },
  { id: "personal_quote", label: "PERSONAL QUOTE" },
  { id: "profile_theme", label: "PROFILE THEME" },
  { id: "profile_song", label: "PROFILE SONG" },
];

export default function VipLoadout() {
  const { user, realUser, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [profileMode, setProfileMode] = useState("mini"); // 'mini' | 'full'
  const [tab, setTab] = useState("username_style");
  const [videoFilter, setVideoFilter] = useState("all");
  const [loadout, setLoadout] = useState({});
  const [initialLoadout, setInitialLoadout] = useState({});
  const [saving, setSaving] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [vipStats, setVipStats] = useState({
    total_vips: 0,
    active_members: [],
    tier_counts: {},
    packages: [],
    benefits: []
  });
  const carouselRef = useRef(null);

  // Determine VIP capability (real user or emulated)
  const isVip = Boolean(user?.is_vip || user?.role === "admin" || user?.role === "root");

  useEffect(() => {
    // Fetch live VIP stats & active VIP members from backend
    api.get("/vip/stats").then((r) => {
      if (r.data) {
        setVipStats(r.data);
      }
    }).catch(() => {
      // Fallback
      api.get("/users/vip").then(res => {
        setVipStats(prev => ({ ...prev, active_members: res.data || [], total_vips: res.data?.length || 0 }));
      }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    if (user?.loadout) {
      setLoadout(user.loadout);
      setInitialLoadout(user.loadout);
    }
  }, [user]);

  const handleModeSwitch = (mode) => {
    setProfileMode(mode);
    if (mode === "mini") {
      setTab("username_style");
    } else {
      setTab("background_video");
    }
  };

  const activeTabs = profileMode === "mini" ? MINI_TABS : FULL_TABS;
  const safeTab = tab;

  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  const handleImportMedia = async (target = "both") => {
    if (!importUrl.trim()) {
      toast.error("Introdu un link valid de Pinterest sau MP4 / GIF!");
      return;
    }
    setImporting(true);
    try {
      const res = await api.post("/loadout/import-media", { url: importUrl.trim(), target });
      if (res.data && res.data.success) {
        setLoadout(res.data.loadout);
        toast.success(`🎉 ${res.data.media_type === "video" ? "Videoclipul" : "Bannerul"} a fost descărcat și aplicat automat pe profil!`);
        setImportUrl("");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la importarea link-ului.");
    } finally {
      setImporting(false);
    }
  };

  // Allow clicking & testing for everyone in Demo Preview
  const handleChange = (key, value) => {
    setLoadout((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!isVip) {
      setShowVipModal(true);
      return;
    }

    setSaving(true);
    try {
      await api.patch("/users/me/loadout", loadout);
      await refresh();
      try {
        queryClient.invalidateQueries();
      } catch (err) {}
      setInitialLoadout(loadout);
      toast.success("Loadout-ul VIP a fost salvat cu succes pe profilul tău!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const scrollCarousel = (dir) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: dir * 260, behavior: "smooth" });
    }
  };

  const unsavedCount = Object.keys(loadout).reduce((acc, k) => (loadout[k] !== initialLoadout[k] ? acc + 1 : acc), 0);

  const previewUser = user 
    ? { ...user, is_vip: true, loadout } 
    : { username: "INDUNGI_MEMBER", is_vip: true, loadout };

  const selectedBadgeObj = BANNER_BADGES.find((b) => b.id === (loadout.banner_badge || "none")) || BANNER_BADGES[0];
  const selectedBgObj = CARD_BACKGROUNDS.find((bg) => bg.id === loadout.card_background);
  const selectedVideoObj = BACKGROUND_VIDEOS.find((v) => v.id === (loadout.background_video || "cs_swat")) || BACKGROUND_VIDEOS[5];
  const selectedThemeId = loadout.profile_theme || "live_identity";
  const selectedThemeObj = PROFILE_THEMES.find((th) => th.id === selectedThemeId) || PROFILE_THEMES[2];
  const previewVideoUrl = loadout.custom_bg_video || (selectedVideoObj && selectedVideoObj.url ? selectedVideoObj.url : null);

  const filteredVideos = videoFilter === "all"
    ? BACKGROUND_VIDEOS
    : BACKGROUND_VIDEOS.filter((v) => v.id === "none" || v.category === videoFilter);

  return (
    <div className="vipLoadoutV2" data-vip-loadout>
      <div className="vipShell max-w-[1400px] mx-auto px-4 py-6">

        {/* 0. DEMO PREVIEW NOTIFICATION BANNER (When user is not VIP) */}
        {!isVip && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-950/80 via-[#0d1017] to-amber-950/80 border border-amber-500/40 shadow-[0_0_25px_rgba(245,158,11,0.2)] flex items-center justify-between flex-wrap gap-4 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 font-bold shadow-[0_0_12px_rgba(245,158,11,0.4)] shrink-0">
                <Sparkles className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black uppercase tracking-wider text-amber-400 bg-amber-500/20 px-2 py-0.5 border border-amber-500/40">
                    PREVIZUALIZARE DEMO VIP
                  </span>
                  <span className="text-xs font-bold text-zinc-300">
                    Testează gratuit toate opțiunile de personalizare în timp real!
                  </span>
                </div>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  Selectează orice stil de nume, ramă animată, armă CS și fundal. Pentru a le afișa permanent pe forum, activează statutul VIP.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowVipModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-chakra font-black text-xs uppercase tracking-wider flex items-center gap-2 shadow-[0_0_15px_rgba(245,158,11,0.5)] transition-all cursor-pointer rounded"
            >
              <Crown className="w-4 h-4" />
              <span>DEVINO VIP ACUM</span>
            </button>
          </div>
        )}

        {/* 1. TOP CAROUSEL: ACTIVE VIP MEMBERS ROSTER */}
        <div className="tfVip px-6 py-4 rounded-xl border border-amber-500/30 bg-[#080b12]/90 shadow-2xl relative mb-6 backdrop-blur-md">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 font-bold text-xs shadow-[0_0_10px_rgba(255,159,28,0.3)]">
                ❖
              </div>
              <div>
                <h3 className="text-xs font-black tracking-widest text-amber-400 uppercase m-0 flex items-center gap-2">
                  MEMBRI CU VIP ACTIV <span className="text-amber-300">({vipStats.total_vips || vipStats.active_members.length})</span>
                </h3>
                <p className="text-[11px] text-zinc-400 m-0">
                  Membrii care sprijină comunitatea INDUNGI și beneficiază de facilități exclusive.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => scrollCarousel(-1)}
                  className="w-7 h-7 rounded bg-black/80 border border-white/10 hover:border-amber-500/60 flex items-center justify-center text-zinc-400 hover:text-amber-400 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollCarousel(1)}
                  className="w-7 h-7 rounded bg-black/80 border border-white/10 hover:border-amber-500/60 flex items-center justify-center text-zinc-400 hover:text-amber-400 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="tfVip-trackWrapper overflow-hidden relative">
            <div
              ref={carouselRef}
              className="js-tfVip-track flex gap-3 overflow-x-auto scrollbar-none py-2 scroll-smooth"
            >
              {vipStats.active_members.map((vMember, idx) => (
                <Link
                  to={`/u/${vMember.username}`}
                  key={idx}
                  className="tfVip-card flex-shrink-0 w-[125px] p-3 rounded-lg border border-amber-500/20 bg-[#05080e] hover:border-amber-400 hover:bg-[#0c101c] transition-all flex flex-col items-center text-center group cursor-pointer"
                >
                  <div className="relative">
                    <Avatar user={vMember} size={56} />
                  </div>
                  <div className="mt-2 text-xs font-bold truncate max-w-full text-zinc-200 group-hover:text-amber-400">
                    {vMember.username}
                  </div>
                  <div className="mt-1 px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40">
                    ★ {vMember.vip_tier ? vMember.vip_tier.replace("_", " ").toUpperCase() : "VIP"}
                  </div>
                  <div className="mt-1 text-[8px] font-mono text-zinc-400 flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5 text-amber-400" />
                    <span>{vMember.vip_expires_at ? formatDistanceToNow(new Date(vMember.vip_expires_at)) : "Permanent"}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* 2. HEADER SECTION */}
        <div className="vipShell__header mb-6">
          <div>
            <p className="vipEyebrow text-amber-400">INDUNGI VIP LOADOUT HUB</p>
            <h1 data-text="PERSONALIZEAZĂ-ȚI IDENTITATEA VIP">PERSONALIZEAZĂ-ȚI IDENTITATEA VIP</h1>
            <p className="vipIntro text-zinc-400">
              Alege stilul numelui, rama avatarului, insigna de rang, fundalul cardului și arma favorită din panoul interactiv.
            </p>
          </div>

          <div className="vipHeaderPreview">
            <span>PREVIZUALIZARE NUME</span>
            <div id="usernamePreview">
              <Username user={previewUser} />
            </div>
          </div>
        </div>

        {/* 3. MODE SWITCHER: MINI PROFILE vs FULL PROFILE */}
        <div className="vmain-modeSwitcher mb-4">
          <button
            onClick={() => handleModeSwitch("mini")}
            className={`vmain-modeBtn vmain-modeBtn--mini ${profileMode === "mini" ? "is-active" : ""}`}
          >
            MINI PROFILE CARD
          </button>
          <button
            onClick={() => handleModeSwitch("full")}
            className={`vmain-modeBtn vmain-modeBtn--full ${profileMode === "full" ? "is-active" : ""}`}
          >
            FULL PROFILE PAGE
          </button>
        </div>

        {/* 4. TABS BAR */}
        <div className="vipTabs mb-6">
          {activeTabs.map((tItem) => (
            <button
              key={tItem.id}
              onClick={() => setTab(tItem.id)}
              className={`vipTab ${safeTab === tItem.id ? "is-active" : ""}`}
            >
              {tItem.label}
            </button>
          ))}
        </div>

        {/* 5. 3-COLUMN MAIN LAYOUT (Selectors + Live Canvas + VIP Statistics Sidebar) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* LEFT SELECTOR COLUMN (Span 5) */}
          <div className="lg:col-span-4 space-y-4">
            <div className="vipCard bg-[#090d14]/90 border border-amber-500/30 rounded-xl p-5 shadow-2xl">

              {/* MINI MODE TABS */}
              {profileMode === "mini" && (
                <>
                  {safeTab === "username_style" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">STILURI DE NUME ANIMATE</h2>
                        <p className="text-xs text-zinc-400">Alege stilul dorit pentru a-l previzualiza în timp real.</p>
                      </div>
                      <div className="selectorGrid--text">
                        {USERNAME_STYLES.map((opt) => {
                          const isSelected = loadout.username_style === opt.id || (!loadout.username_style && opt.id === "default");
                          return (
                            <button
                              key={opt.id}
                              onClick={() => handleChange("username_style", opt.id)}
                              className={`selectorItem ${isSelected ? "active" : ""}`}
                            >
                              <Username 
                                user={{ username: opt.label, loadout: { username_style: opt.id } }} 
                                asText={true} 
                                hover={false} 
                              />
                              {!isVip && opt.requiresVip && (
                                <span className="absolute top-1 right-1 text-[8px] bg-amber-500/20 text-amber-300 px-1 rounded border border-amber-500/30">
                                  DEMO
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {safeTab === "avatar_frame" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">RAME AVATAR EXCLUSIVE</h2>
                        <p className="text-xs text-zinc-400">Selectează rama animată sau tactică pentru avatarul tău.</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2.5 max-h-[460px] overflow-y-auto pr-1 scrollbar-thin">
                        {AVATAR_FRAMES.map((f) => {
                          const isSelected = (loadout.avatar_frame || "none") === f.id;
                          return (
                            <button
                              key={f.id}
                              onClick={() => handleChange("avatar_frame", f.id)}
                              className={`selectorItem p-2.5 flex flex-col items-center justify-center text-center rounded-lg border transition-all ${
                                isSelected 
                                  ? "active border-amber-400 bg-amber-500/10 shadow-[0_0_12px_rgba(245,158,11,0.25)]" 
                                  : "border-white/10 hover:border-white/20 bg-black/40"
                              }`}
                            >
                              <div className="w-14 h-14 rounded-lg bg-black/70 border border-white/10 flex items-center justify-center relative mb-2 overflow-hidden">
                                {f.id === "none" ? (
                                  <X className="w-6 h-6 text-rose-400" />
                                ) : f.imageUrl ? (
                                  <img 
                                    src={f.imageUrl} 
                                    alt={f.label} 
                                    className="w-12 h-12 object-contain pointer-events-none drop-shadow-md"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className={`w-10 h-10 rounded-full border-2 ${f.className || "border-amber-400"}`} />
                                )}
                              </div>
                              <span className="text-[10px] font-bold text-zinc-200 uppercase tracking-wider truncate max-w-full">
                                {f.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {safeTab === "banner_badge" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">INSIGNE & BANNER VIP</h2>
                        <p className="text-xs text-zinc-400">Alege emblema care apare pe profilul și cardul tău.</p>
                      </div>
                      <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1 scrollbar-thin">
                        {BANNER_BADGES.map((b) => {
                          const isSelected = (loadout.banner_badge || "none") === b.id;
                          return (
                            <button
                              key={b.id}
                              onClick={() => handleChange("banner_badge", b.id)}
                              className={`selectorItem w-full p-3 flex items-center justify-between rounded-lg border transition-all ${
                                isSelected 
                                  ? "active border-amber-400 bg-amber-500/10 shadow-[0_0_12px_rgba(245,158,11,0.25)]" 
                                  : "border-white/10 hover:border-white/20 bg-black/40"
                              }`}
                            >
                              <span className="text-xs font-bold text-zinc-200">{b.label}</span>
                              {b.id !== "none" ? (
                                <span className={`userBanner ${b.className || "userBanner--ForumsvipGold"} text-[10px] py-0.5 px-2.5`}>
                                  <span className="userBanner-before"></span>
                                  <strong>{b.text || b.label}</strong>
                                  <span className="userBanner-after"></span>
                                </span>
                              ) : (
                                <span className="text-[10px] text-zinc-500 font-mono">Fără Insignă</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {safeTab === "card_background" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">FUNDALURI CARD PROFIL</h2>
                        <p className="text-xs text-zinc-400">Alege textura și gradientul pentru cardul de utilizator.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5 max-h-[460px] overflow-y-auto pr-1 scrollbar-thin">
                        {CARD_BACKGROUNDS.map((bg) => {
                          const isSelected = (loadout.card_background || "none") === bg.id;
                          return (
                            <button
                              key={bg.id}
                              onClick={() => handleChange("card_background", bg.id)}
                              className={`selectorItem h-20 p-2.5 flex flex-col justify-end text-left rounded-lg relative overflow-hidden border transition-all ${bg.className || ""} ${
                                isSelected ? "active border-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.4)]" : "border-white/10 hover:border-white/20"
                              }`}
                              style={bg.style}
                            >
                              <div className="absolute inset-0 bg-black/40 pointer-events-none" />
                              <span className="relative z-10 text-[10px] font-black text-white uppercase tracking-wider drop-shadow">
                                {bg.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Direct Link Importer (Pinterest / GIF / Image) */}
                      <div className="mt-4 p-3.5 rounded-xl bg-gradient-to-r from-amber-500/10 via-[#070a12] to-amber-500/5 border border-amber-500/30 space-y-2.5">
                        <label className="text-xs font-black text-amber-400 flex items-center gap-1.5 font-chakra uppercase tracking-wider">
                          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                          <span>IMPORTĂ BANNER DIRECT PRIN LINK (PINTEREST / GIF / IMAGINE)</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={importUrl}
                            onChange={(e) => setImportUrl(e.target.value)}
                            placeholder="Ex: https://de.pinterest.com/pin/123456... sau https://site.com/animatie.gif"
                            className="flex-1 bg-black/70 border border-white/15 rounded-lg px-3 py-2 text-xs text-zinc-100 placeholder-zinc-500 focus:border-amber-400 outline-none font-mono"
                          />
                          <button
                            onClick={() => handleImportMedia("card_background")}
                            disabled={importing || !importUrl.trim()}
                            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black text-xs uppercase tracking-wider rounded-lg transition-all disabled:opacity-50 flex items-center gap-1.5 shadow-md shadow-amber-500/20 whitespace-nowrap"
                          >
                            {importing ? (
                              <>
                                <span className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                <span>Importare...</span>
                              </>
                            ) : (
                              <>
                                <Download className="w-3.5 h-3.5" />
                                <span>DESCARCĂ & APLICĂ</span>
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-[10px] text-zinc-400">
                          Lipește orice link de Pinterest sau link direct de GIF/MP4. Sistemul îl descarcă automat pe server și îl aplică instant pe profilul tău!
                        </p>
                      </div>
                    </>
                  )}

                  {safeTab === "favorite_weapon" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">ARMĂ FAVORITĂ (CS2 / CS 1.6)</h2>
                        <p className="text-xs text-zinc-400">Selectează arma preferată afișată pe profilul tău.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5 max-h-[460px] overflow-y-auto pr-1 scrollbar-thin">
                        {WEAPONS.map((w) => {
                          const isSelected = (loadout.favorite_weapon || "AK-47") === w.name;
                          return (
                            <button
                              key={w.id || w.name}
                              onClick={() => handleChange("favorite_weapon", w.name)}
                              className={`selectorItem p-2.5 flex items-center gap-3 rounded-lg border transition-all ${
                                isSelected 
                                  ? "active border-amber-400 bg-amber-500/10 shadow-[0_0_12px_rgba(245,158,11,0.2)]" 
                                  : "border-white/10 hover:border-white/20 bg-black/40"
                              }`}
                            >
                              <div className="w-12 h-8 bg-black/60 rounded flex items-center justify-center p-1 border border-white/5 shrink-0">
                                <img src={w.iconUrl} alt={w.name} className="h-6 w-full object-contain filter drop-shadow" />
                              </div>
                              <span className="text-xs font-bold text-zinc-200 truncate">{w.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* FULL PROFILE MODE TABS */}
              {profileMode === "full" && (
                <>
                  {safeTab === "profile_bundles" && (
                    <>
                      <div className="vipCard__title mb-4 flex items-center justify-between">
                        <div>
                          <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider flex items-center gap-2">
                            <Package className="w-4 h-4 text-amber-400" />
                            <span>PACHETE COMPLETE DISCORD & VIP</span>
                          </h2>
                          <p className="text-xs text-zinc-400">Pachete tematice all-in-one: Avatar Frame + Efect Profil Animant + Temă + Badge.</p>
                        </div>
                        <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                          {PROFILE_BUNDLES.length} PACHETE
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[460px] overflow-y-auto pr-1 scrollbar-thin mb-4">
                        {PROFILE_BUNDLES.map((bundle) => {
                          const isCurrentActive = loadout.background_video === bundle.background_video && loadout.avatar_frame === bundle.avatar_frame;
                          return (
                            <div
                              key={bundle.id}
                              className={`p-3.5 rounded-xl border relative overflow-hidden transition-all duration-300 flex flex-col justify-between ${
                                isCurrentActive
                                  ? "border-amber-400 bg-[#080d1a] shadow-[0_0_20px_rgba(245,158,11,0.25)]"
                                  : "border-white/10 bg-[#05070c] hover:border-white/20 hover:bg-[#070b14]"
                              }`}
                            >
                              {/* Background Glow accent */}
                              <div
                                className="absolute -top-12 -right-12 w-28 h-28 rounded-full blur-2xl opacity-20 pointer-events-none"
                                style={{ background: bundle.color }}
                              />

                              <div>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
                                      <span className="w-2 h-2 rounded-full" style={{ background: bundle.color }} />
                                      <span>{bundle.category.toUpperCase()}</span>
                                    </div>
                                    <h3 className="text-xs font-black text-white mt-0.5">{bundle.name}</h3>
                                  </div>
                                  <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 whitespace-nowrap">
                                    {bundle.price}
                                  </span>
                                </div>

                                <p className="text-[11px] text-zinc-400 mb-3">{bundle.tagline}</p>

                                {/* Items included */}
                                <div className="space-y-1 mb-4 bg-black/40 p-2 rounded-lg border border-white/5">
                                  <div className="text-[9px] font-bold text-zinc-500 uppercase">Articole Incluse:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {bundle.items.map((item, idx) => (
                                      <span key={idx} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/5">
                                        ✨ {item.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              <button
                                onClick={() => {
                                  setLoadout((prev) => ({
                                    ...prev,
                                    avatar_frame: bundle.avatar_frame,
                                    banner_badge: bundle.banner_badge,
                                    profile_theme: bundle.profile_theme,
                                    background_video: bundle.background_video,
                                    username_style: bundle.username_style,
                                    favorite_weapon: bundle.favorite_weapon,
                                    personal_quote: bundle.personal_quote,
                                    custom_bg_video: ""
                                  }));
                                  toast.success(`Pachetul "${bundle.name}" a fost echipat!`);
                                }}
                                className={`w-full py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
                                  isCurrentActive
                                    ? "bg-amber-400 text-black font-black shadow-[0_0_12px_rgba(245,158,11,0.5)]"
                                    : "bg-white/10 text-white hover:bg-white/20"
                                }`}
                              >
                                {isCurrentActive ? (
                                  <>
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    <span>PACHET ECHIPAT</span>
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                                    <span>ECHIPEAZĂ TOT PACHETUL</span>
                                  </>
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {safeTab === "background_video" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">VIDEO FUNDAL PROFIL</h2>
                        <p className="text-xs text-zinc-400">Alege o buclă video animată sau adaugă propriul tău link video direct.</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2.5 max-h-[420px] overflow-y-auto pr-1 scrollbar-thin mb-4">
                        {filteredVideos.map((vid) => {
                          const isSelected = (loadout.background_video || "cs_smoke_fire") === vid.id && !loadout.custom_bg_video;
                          return (
                            <button
                              key={vid.id}
                              onClick={() => {
                                handleChange("background_video", vid.id);
                                handleChange("custom_bg_video", "");
                              }}
                              className={`selectorItem h-24 p-2 relative rounded-lg overflow-hidden border transition-all ${
                                isSelected ? "active border-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.4)]" : "border-white/10 hover:border-white/20"
                              }`}
                            >
                              {vid.id === "none" ? (
                                <div className="flex flex-col items-center justify-center h-full text-zinc-500 bg-black/60">
                                  <X className="w-5 h-5 text-red-500 mb-1" />
                                  <span className="text-[9px] font-bold">FĂRĂ VIDEO</span>
                                </div>
                              ) : (
                                <>
                                  <img src={vid.thumb} alt={vid.label} className="absolute inset-0 w-full h-full object-cover opacity-60" />
                                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                    <Play className="w-5 h-5 text-white/80 fill-white" />
                                  </div>
                                  <span className="absolute bottom-1 left-2 text-[9px] font-black text-white uppercase drop-shadow">
                                    {vid.label}
                                  </span>
                                </>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Direct Link Importer (Pinterest / MP4 / WebM) */}
                      <div className="p-3.5 rounded-xl bg-gradient-to-r from-amber-500/10 via-[#070a12] to-amber-500/5 border border-amber-500/30 space-y-2.5">
                        <label className="text-xs font-black text-amber-400 flex items-center gap-1.5 font-chakra uppercase tracking-wider">
                          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                          <span>IMPORTĂ DIRECT DE PE PINTEREST / VIDEO PROPRIU (MP4 / WEBM)</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={importUrl}
                            onChange={(e) => setImportUrl(e.target.value)}
                            placeholder="Ex: https://de.pinterest.com/pin/970525... sau https://site.com/video.mp4"
                            className="flex-1 bg-black/70 border border-white/15 rounded-lg px-3 py-2 text-xs text-zinc-100 placeholder-zinc-500 focus:border-amber-400 outline-none font-mono"
                          />
                          <button
                            onClick={() => handleImportMedia("background_video")}
                            disabled={importing || !importUrl.trim()}
                            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black text-xs uppercase tracking-wider rounded-lg transition-all disabled:opacity-50 flex items-center gap-1.5 shadow-md shadow-amber-500/20 whitespace-nowrap"
                          >
                            {importing ? (
                              <>
                                <span className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                <span>Importare...</span>
                              </>
                            ) : (
                              <>
                                <Download className="w-3.5 h-3.5" />
                                <span>DESCARCĂ & APLICĂ</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="text-[10px] text-zinc-400">
                          Descarcă automat fișierul original HD de pe Pinterest și îl aplică în fundalul întregului tău profil!
                        </div>
                      </div>
                    </>
                  )}

                  {safeTab === "personal_quote" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">CITAT PERSONALIZAT</h2>
                        <p className="text-xs text-zinc-400">Mesaj afișat în secțiunea de profil.</p>
                      </div>
                      <textarea
                        value={loadout.personal_quote || ""}
                        onChange={(e) => handleChange("personal_quote", e.target.value.substring(0, 200))}
                        rows={4}
                        className="w-full bg-[#05080e] border border-amber-500/20 rounded-lg p-3 text-xs text-zinc-200 focus:border-amber-400 outline-none"
                        placeholder="ex: 'Play to win, not to participate.'"
                      />
                      <div className="text-[10px] text-zinc-500 text-right mt-1">
                        {(loadout.personal_quote || "").length}/200
                      </div>
                    </>
                  )}

                  {safeTab === "profile_theme" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">STILURI PROFIL TEMATICE</h2>
                        <p className="text-xs text-zinc-400">Alege estetica paginii tale de profil conectată la nivelul tău VIP.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 max-h-[460px] overflow-y-auto pr-1 scrollbar-thin">
                        {PROFILE_THEMES.map((th) => {
                          const isSelected = (loadout.profile_theme || "live_identity") === th.id;
                          return (
                            <button
                              key={th.id}
                              onClick={() => handleChange("profile_theme", th.id)}
                              className={`selectorItem p-3.5 text-left rounded-lg border transition-all ${
                                isSelected 
                                  ? "active border-amber-400 bg-amber-500/10 shadow-[0_0_15px_rgba(245,158,11,0.25)]" 
                                  : "border-white/10 hover:border-white/20 bg-black/40"
                              }`}
                              style={isSelected ? { borderColor: th.color } : {}}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-black uppercase font-chakra" style={{ color: th.color }}>
                                  {th.label}
                                </span>
                                {isSelected && <span className="w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: th.color }} />}
                              </div>
                              <div className="text-[10px] text-zinc-400 font-mono">{th.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {safeTab === "profile_song" && (
                    <>
                      <div className="vipCard__title mb-4">
                        <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">MUZICĂ PROFIL (YOUTUBE)</h2>
                        <p className="text-xs text-zinc-400">Inserează link-ul sau ID-ul piesei de pe YouTube.</p>
                      </div>
                      <input
                        type="text"
                        value={loadout.profile_song || ""}
                        onChange={(e) => handleChange("profile_song", e.target.value)}
                        className="w-full bg-[#05080e] border border-amber-500/20 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-amber-400 outline-none"
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                    </>
                  )}
                </>
              )}

            </div>
          </div>

          {/* MIDDLE PREVIEW CANVAS (Span 4) */}
          <div className="lg:col-span-4 flex flex-col items-center">
            <div className="w-full bg-[#090d14]/90 border border-amber-500/30 rounded-xl p-5 shadow-2xl backdrop-blur-md">
              <span className="text-[10px] font-black uppercase tracking-wider text-amber-400 block mb-3 font-mono">
                {profileMode === "mini" ? "PREVIZUALIZARE CARD LIVE" : "PREVIZUALIZARE PROFIL COMPLET"}
              </span>

              {profileMode === "mini" ? (
                <div
                  className={`vmain-memberTooltipPreview w-full rounded-xl overflow-hidden border border-amber-500/30 p-4 relative shadow-lg ${selectedBgObj?.className || ""}`}
                  style={
                    loadout.custom_card_banner && !loadout.custom_card_banner.endsWith(".mp4")
                      ? { backgroundImage: `url(${loadout.custom_card_banner})`, backgroundSize: "cover", backgroundPosition: "center" }
                      : (selectedBgObj?.style || { background: "#060910" })
                  }
                >
                  {/* Live Background Video in Mini Card */}
                  {(loadout.custom_card_banner?.endsWith(".mp4") || (loadout.card_background === "custom" && loadout.custom_bg_video?.endsWith(".mp4"))) && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                      <video
                        key={loadout.custom_card_banner || loadout.custom_bg_video}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover opacity-80 filter saturate-125 brightness-100"
                        src={loadout.custom_card_banner || loadout.custom_bg_video}
                      />
                      <div className="absolute inset-0 bg-black/40 pointer-events-none" />
                    </div>
                  )}

                  <div className="vmain-memberTooltipHeader flex items-center gap-3 relative z-10">
                    <div className="relative">
                      <Avatar user={previewUser} size={80} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-lg font-black font-chakra uppercase">
                        <Username user={previewUser} />
                      </div>
                      <div className="mt-1">
                        <span className={`userBanner ${selectedBadgeObj?.className || "userBanner--ForumsvipGold"} text-[10px] py-0.5 px-2`}>
                          <span className="userBanner-before"></span>
                          <strong>{selectedBadgeObj?.text || selectedBadgeObj?.label || "FORUMS VIP"}</strong>
                          <span className="userBanner-after"></span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-white/10 text-center font-mono text-[10px]">
                    <div className="p-1.5 bg-black/40 rounded">
                      <div className="text-zinc-500">MESSAGES</div>
                      <div className="text-white font-bold text-xs">{user?.message_count || 128}</div>
                    </div>
                    <div className="p-1.5 bg-black/40 rounded">
                      <div className="text-zinc-500">REACTION</div>
                      <div className="text-amber-400 font-bold text-xs">{user?.reaction_score || 45}</div>
                    </div>
                    <div className="p-1.5 bg-black/40 rounded col-span-2 flex items-center justify-between px-3">
                      <span className="text-zinc-500">ARMĂ FAVORITĂ:</span>
                      <span className="text-cyan-400 font-bold">{loadout.favorite_weapon || "AK-47"}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  className="rounded-xl bg-[#070a10] p-5 relative overflow-hidden shadow-2xl space-y-3 transition-all duration-300 border"
                  style={{
                    borderColor: selectedThemeObj.color || "rgba(245,158,11,0.5)",
                    boxShadow: `0 15px 40px ${selectedThemeObj.color || "rgba(245,158,11,0.5)"}30`
                  }}
                >
                  {/* Live Animated Canvas or Video Background in Preview */}
                  {(loadout.background_video !== "none") && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 rounded-xl">
                      <AnimatedBackground 
                        type={loadout.background_video || "cosmic_nebula"} 
                        customUrl={loadout.custom_bg_video} 
                        className="!absolute !w-full !h-full" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/90 pointer-events-none" />
                    </div>
                  )}

                  <div 
                    className="h-1.5 w-full -mt-5 -mx-5 mb-4 shadow-sm relative z-10" 
                    style={{
                      background: selectedThemeId === "bronze_identity" 
                        ? "linear-gradient(90deg, #cd7f32, #e5a65d, #cd7f32)"
                        : selectedThemeId === "arctic_identity"
                        ? "linear-gradient(90deg, #00e5ff, #3b82f6, #14b8a6)"
                        : selectedThemeId === "diamond_identity"
                        ? "linear-gradient(90deg, #8b5cf6, #00e5ff, #ec4899)"
                        : selectedThemeId === "founder_identity"
                        ? "linear-gradient(90deg, #ffd700, #f59e0b, #ffd700)"
                        : selectedThemeId === "default"
                        ? "linear-gradient(90deg, #52525b, #a1a1aa, #52525b)"
                        : "linear-gradient(90deg, #f59e0b, #ea580c, #f59e0b)"
                    }}
                  />
                  <div className="flex items-center gap-4 relative z-10">
                    <Avatar user={previewUser} size={84} />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-xl font-black text-white font-chakra">
                        <Username user={previewUser} />
                      </div>
                      <div className="mt-1">
                        <span className={`userBanner ${selectedBadgeObj?.className || "userBanner--ForumsvipGold"} text-[10px] py-0.5 px-2.5`}>
                          <span className="userBanner-before"></span>
                          <strong>{selectedBadgeObj?.text || selectedBadgeObj?.label || "VIP MEMBER"}</strong>
                          <span className="userBanner-after"></span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-2.5 rounded bg-black/50 border border-white/5 text-xs text-amber-300 font-mono italic text-left">
                    "{loadout.personal_quote || "Tactical Fragging Master"}"
                  </div>

                  <div className="flex items-center justify-between text-xs font-mono text-zinc-400 pt-2 border-t border-white/5">
                    <span>Armă: <strong className="text-cyan-400">{loadout.favorite_weapon || "AK-47"}</strong></span>
                    <span>Temă Activă: <strong style={{ color: selectedThemeObj.color }}>{selectedThemeObj.label}</strong></span>
                  </div>
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                <button
                  onClick={() => setLoadout(initialLoadout)}
                  className="px-3 py-2 bg-[#121622] hover:bg-[#1a2030] text-zinc-300 text-xs font-bold uppercase rounded-lg border border-white/10 transition-all"
                >
                  RESETEAZĂ
                </button>

                {isVip ? (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black text-xs font-black uppercase rounded-lg font-chakra tracking-wider shadow-[0_0_15px_rgba(245,158,11,0.5)] transition-all cursor-pointer flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    <span>{saving ? "SE SALVEAZĂ..." : "SALVEAZĂ LOADOUT"}</span>
                  </button>
                ) : (
                  <Link
                    to="/donate?tab=vip"
                    target="_blank"
                    className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black text-xs font-black uppercase rounded-lg font-chakra tracking-wider shadow-[0_0_15px_rgba(245,158,11,0.5)] transition-all cursor-pointer flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    <span>DEVINO VIP PENTRU ACTIVARE</span>
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT VIP STATISTICS & VIP MEMBERS SIDEBAR (Span 4) */}
          <div className="lg:col-span-4 space-y-4">
            
            {/* VIP STATISTICS WIDGET */}
            <div className="bg-[#090d14]/90 border border-amber-500/30 rounded-xl p-5 shadow-2xl backdrop-blur-md">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-amber-500/20">
                <Crown className="w-4 h-4 text-amber-400" />
                <h3 className="text-xs font-black tracking-widest text-amber-400 uppercase">
                  STATISTICI & MEMBRI VIP
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-[#06080e] border border-amber-500/20 text-center">
                  <div className="text-[10px] font-bold text-zinc-400 uppercase">TOTAL MEMBRI VIP</div>
                  <div className="text-2xl font-black text-amber-400 font-chakra mt-1">
                    {vipStats.total_vips || vipStats.active_members.length}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-[#06080e] border border-amber-500/20 text-center">
                  <div className="text-[10px] font-bold text-zinc-400 uppercase">BENEFICII ACTIVE</div>
                  <div className="text-2xl font-black text-emerald-400 font-chakra mt-1">
                    5/5
                  </div>
                </div>
              </div>

              {/* ACTIVE VIP MEMBERS LIST */}
              <div className="space-y-2 mb-4">
                <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>LISTĂ MEMBRI VIP ACTIVI</span>
                  <span className="text-amber-400 font-mono text-[9px]">DURATĂ RĂMASĂ</span>
                </div>

                <div className="max-h-[260px] overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
                  {vipStats.active_members.map((m, idx) => (
                    <div 
                      key={idx}
                      className="p-2 rounded bg-[#060910] border border-white/5 hover:border-amber-500/30 flex items-center justify-between gap-2 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar user={m} size={28} />
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-zinc-200 truncate flex items-center gap-1">
                            <span>{m.username}</span>
                            {m.vip_tier === "founder" && <span className="text-[8px] text-amber-400">👑</span>}
                          </div>
                          <div className="text-[9px] text-zinc-500">
                            {m.vip_purchase_count ? `Achiziții: x${m.vip_purchase_count}` : "VIP Sprijinitor"}
                          </div>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
                          {m.vip_expires_at ? formatDistanceToNow(new Date(m.vip_expires_at)) : "Permanent"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* VIP PACKAGES & DURATIONS */}
              <div className="pt-3 border-t border-amber-500/20 space-y-2">
                <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                  PACHETE & DURATE VIP DISPONIBILE
                </div>
                <div className="space-y-1.5">
                  {(vipStats.packages || [
                    { name: "VIP Starter (7 Zile)", price: "100 Puncte / 3€" },
                    { name: "VIP Pro (30 Zile)", price: "350 Puncte / 8€", popular: true },
                    { name: "VIP Season (90 Zile)", price: "900 Puncte / 20€" },
                    { name: "VIP Permanent (Lifetime)", price: "2500 Puncte / 50€" },
                  ]).map((pkg, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-[#06080e] border border-white/5">
                      <span className="font-bold text-zinc-300">{pkg.name}</span>
                      <span className="text-amber-400 font-mono font-bold text-[11px]">{pkg.price}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowVipModal(true)}
                  className="w-full mt-3 py-2 bg-amber-500/20 hover:bg-amber-500 hover:text-black border border-amber-500/40 text-amber-300 text-xs font-bold uppercase rounded font-chakra tracking-wider flex items-center justify-center gap-1.5 transition-all"
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  <span>CUMPĂRĂ / ACTIVEAZĂ VIP</span>
                </button>
              </div>

            </div>

          </div>

        </div>

      </div>

      {/* VIP PURCHASE / ACTIVATE MODAL */}
      {showVipModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-[300] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setShowVipModal(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="bg-[#090d14] border border-amber-500/40 rounded-xl max-w-lg w-full p-6 space-y-4 shadow-[0_0_40px_rgba(245,158,11,0.3)] relative"
          >
            <div className="flex items-center justify-between pb-3 border-b border-amber-500/20">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-black text-amber-400 uppercase font-chakra tracking-wider">
                  DEBLOCHARE VIP LOADOUT
                </h3>
              </div>
              <button onClick={() => setShowVipModal(false)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed">
              Statutul VIP îți oferă acces instant la toate stilurile de nume animate, ramele holografice, insignele speciale și armele personalizate.
            </p>

            <div className="space-y-2">
              <div className="text-[10px] font-bold text-zinc-400 uppercase">Alege durata pachetului dorit:</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: "VIP Starter (7 Zile)", price: "100 Puncte" },
                  { name: "VIP Pro (30 Zile)", price: "350 Puncte", popular: true },
                  { name: "VIP Season (90 Zile)", price: "900 Puncte" },
                  { name: "VIP Permanent", price: "2500 Puncte" },
                ].map((p, idx) => (
                  <div key={idx} className="p-3 bg-[#06080e] border border-amber-500/20 rounded hover:border-amber-400 transition-all cursor-pointer">
                    <div className="text-xs font-bold text-white">{p.name}</div>
                    <div className="text-amber-400 text-xs font-bold font-mono mt-1">{p.price}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t border-amber-500/20 flex gap-2 justify-end">
              <button 
                onClick={() => setShowVipModal(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold uppercase rounded"
              >
                Închide
              </button>
              <Link 
                to="/donate"
                onClick={() => setShowVipModal(false)}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black text-xs font-black uppercase rounded font-chakra flex items-center gap-1.5"
              >
                <span>Mergi la Donații / Puncte</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
